import type { CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * LE JEU DE DEMONSTRATION de l'app Timesheet : deux semaines de temps passe
 * dans une boite de production video luxembourgeoise, 3 clients, 5 projets et
 * 33 entrees de journal.
 *
 * CE N'EST PAS UN PLANNING BIEN RANGE, ET C'EST TOUT L'OBJET. Le brief le dit
 * en une phrase : « un jeu bien reparti ne montre aucun defaut ». Chaque
 * chiffre ci-dessous est cale sur un favori, un compteur, une carte liee ou une
 * case de la grille, pour qu'aucun ne rende une liste vide par hasard ni la
 * totalite du jeu :
 *
 *   Journal, favori « Production »  -> 21 entrees sur 33. Le filtre en retire
 *                                      donc 12 (6 preparation, 3 deplacement,
 *                                      3 administratif) : on voit qu'il filtre.
 *   Projets, favori « En cours »    -> 2 projets sur 5.
 *   Projets, carte « Heures »       -> « Terres Rouges » en porte 14 (34 h 45
 *                                      de « Temps saisi »), « Sentiers de
 *                                      l'Our » AUCUNE : sa carte liee est VIDE,
 *                                      et il faut voir a quoi ressemble une
 *                                      carte sans rien dedans autant que le
 *                                      contraire.
 *   Societes, carte « Projets »      -> 2, 2 et 1 : trois comptes differents,
 *                                      jamais zero, jamais le meme.
 *   Societes, favori « Clients »     -> les 3 societes de ce jeu sont des
 *                                      clients. La porte lit la famille
 *                                      `contacts` ENTIERE de l'organisation
 *                                      (toutes apps confondues) : c'est le
 *                                      carnet de l'app Contact et le seed local
 *                                      qui apportent les non-clients, et le
 *                                      favori les met de cote.
 *   Ma semaine, le week-end         -> UNE entree le samedi, pour une seule des
 *                                      deux personnes : le samedi deplie a
 *                                      quelque chose a montrer, et le dimanche
 *                                      reste vide.
 *
 * L'ASYMETRIE QUE LE FONDATEUR A TRANCHEE, la seule qu'il ait imposee : deux
 * projets « En cours » concentrent le gros des heures (34 h 45 et 17 h 30 sur
 * 64 h au total), un projet « Livre » n'en garde que 4 h, un projet « Clos »
 * en a 6 h 30 mais PLUS AUCUNE cette semaine (toutes dans la semaine
 * precedente), et le projet « Cadrage » n'en a aucune.
 *
 * DEUX PERSONNES, ET ELLES NE SE RESSEMBLENT PAS : Celia (`test@taki.lu`,
 * l'owner du seed local, donc celle qui ouvre l'app) tient 19 entrees, Marc
 * (`membre@taki.lu`) 14. Un jeu ou les deux ont le meme volume ne montrerait
 * jamais que « Par intervenant » regroupe vraiment.
 *
 * DEUX ENTREES NE DESIGNENT AUCUN PROJET (un point d'equipe, des notes de
 * frais) : la colonne Projet du tableau doit parfois etre vide, sinon rien ne
 * prouve que l'ecran sait se taire.
 *
 * LES REGLES DU PACK, tenues ici :
 * - toutes les ecritures passent par `@taki/sdk` (`sdk.contacts.*`,
 *   `sdk.projects.*`, `sdk.timesheets.*`), aucun acces Firebase ;
 * - IDEMPOTENT : le SDK ne laisse pas choisir l'id d'un document, chaque ligne
 *   porte donc un `customFields.demoKey` stable (`ts-cl-*`, `ts-pr-*`,
 *   `ts-en-*`). L'import releve les clefs deja presentes et ne cree QUE ce qui
 *   manque ; le relancer ne duplique rien ; c'est aussi ce qui permet a un
 *   projet de POINTER son client, et a une entree de pointer son projet, sans
 *   connaitre leur id. `demoKey` n'est PAS declare dans `taki.fields.json` :
 *   c'est une clef du sac que l'ecran ne montre pas, et qui ne sert qu'ici ;
 * - PAS de suppression : le client n'en a pas le droit (rules deny-by-default).
 *   Pour repartir de zero, `pnpm dev:local --fresh` ;
 * - AUCUNE DATE FIGEE. Tout se calcule a l'import, depuis le LUNDI DE LA
 *   SEMAINE COURANTE en heure LOCALE (voir `lundiDeLaSemaine`) : le jeu tombe
 *   donc toujours sur « cette semaine » et « la semaine derniere », quel que
 *   soit le jour ou on l'importe ;
 * - LES VALEURS SONT LES VALEURS STOCKEES du catalogue (`etat: 'enCours'`,
 *   `relation: 'client'`), jamais les libelles : c'est
 *   `taki.fields.json` qui fait foi, et l'ecran affiche le libelle.
 *
 * L'ORDRE D'ECRITURE N'EST PAS LIBRE : les clients d'abord, les projets
 * ensuite (un projet pointe son client par `contactId`), leurs taches (une tache
 * pointe son projet par `projectId`), le journal en dernier
 * (une entree pointe son projet par `projectId`). On relit donc chaque jeu
 * APRES ecriture, et la table `demoKey -> id` resout la reference.
 *
 * LES MAISONS SONT INVENTEES : aucune societe reelle n'apparait ici, et les
 * deux intervenants sont les comptes de l'emulateur local
 * (`scripts/compte-de-test.mjs`), pas des personnes.
 */

/* ------------------------------------------------------------------ */
/* Les trois types locaux                                              */
/* ------------------------------------------------------------------ */

/**
 * LES TROIS TYPES, ECRITS ICI ET PAS IMPORTES.
 *
 * `journal` et `projet` sont les types LOCAUX que `taki.app.json` declare pour
 * cette app ; `organization` est un discriminant FERME de la famille `contacts`
 * (cf. `CONTACT_TYPES` du data-model), une constante du socle. Les recopier ici
 * garde le jeu de demonstration lisible sans dependre d'un autre module.
 */
const TYPE_ENTREE = 'journal';
const TYPE_PROJET = 'projet';
const TYPE_TACHE = 'tache';
const TYPE_SOCIETE = 'organization';

/**
 * LES DEUX INTERVENANTS, par leur ADRESSE : les comptes du seed local, owner
 * puis member. Ce n'est PAS ce qui s'ecrit : `workerId` porte le `personId` de
 * la personne (chantier `champ-personne`), et l'adresse ne sert qu'a la
 * RETROUVER dans l'annuaire des personnes, a l'import.
 */
const CELIA = 'test@taki.lu';
const MARC = 'membre@taki.lu';

/* ------------------------------------------------------------------ */
/* Ce que l'import rend                                                */
/* ------------------------------------------------------------------ */

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

/* ------------------------------------------------------------------ */
/* Les 3 clients                                                       */
/* ------------------------------------------------------------------ */

/**
 * TROIS MAISONS, TROIS FACONS DE COMMANDER UNE VIDEO : une chaine (des series
 * a livrer par episode), une institution (un film de marque et une captation
 * d'evenement), une marque (un spot court). C'est le vocabulaire de la section
 * 2 du brief, pas « Client A » : un jeu invraisemblable ne permet de juger
 * rien.
 *
 * ILS SONT ECRITS DANS LA FAMILLE `contacts`, EN SOCIETES, exactement comme
 * l'app Contact les ecrit : la porte Clients de Timesheet lit ce jeu tel quel,
 * et la fiche qui s'y ouvre est celle que Contact edite, pas une copie.
 */
interface DemoClient {
  demoKey: string;
  /** Raison sociale : champ NATIF, exige a la creation par les rules. */
  name: string;
  email: string;
  phone: string;
  /** Relation, en valeur STOCKEE du catalogue (`client`, `supplier`...). */
  relation: string;
}

export const DEMO_CLIENTS: ReadonlyArray<DemoClient> = [
  /* LA CHAINE : deux projets, dont celui qui porte le gros des heures. */
  {
    demoKey: 'ts-cl-1',
    name: 'Télé Lëtzebuerg SA',
    email: 'productions@tele-letzebuerg.lu',
    phone: '+352 26 44 18 00',
    relation: 'client',
  },
  /* L'INSTITUTION : un film de marque en cours, une captation close. */
  {
    demoKey: 'ts-cl-2',
    name: 'Fondation Belval pour la Mémoire Industrielle',
    email: 'communication@fondation-belval.lu',
    phone: '+352 27 55 90 40',
    relation: 'client',
  },
  /* LA MARQUE : un seul projet, livré. Son compte « Projets » vaut 1. */
  {
    demoKey: 'ts-cl-3',
    name: 'Brasserie Clausener SA',
    email: 'marketing@brasserie-clausener.lu',
    phone: '+352 43 21 07 55',
    relation: 'client',
  },
];

/* ------------------------------------------------------------------ */
/* Les 5 projets                                                       */
/* ------------------------------------------------------------------ */

/**
 * CINQ PROJETS, ET LEUR ETAT EST CE QUI REND LA PORTE LISIBLE : deux
 * « En cours » qui concentrent les heures, un « Livre », un « Clos » dont plus
 * rien ne bouge, un « Cadrage » que personne n'a encore commence. C'est la
 * seule chose que le fondateur ait tranchee, et elle se lit d'un coup d'oeil en
 * kanban comme au tableau.
 *
 * LES DATES SONT EN JOURS DEPUIS LE LUNDI DE LA SEMAINE COURANTE, negatifs vers
 * le passe : le jeu vieillit avec le calendrier, il ne se fige jamais.
 *
 * MIDI LOCAL, ET PAS 00:00 : la famille `projects` porte `startAt` et `dueAt`
 * en dates-heures (`z.date()`), pas en chaines `YYYY-MM-DD`. Une date de jour
 * posee a minuit glisse d'un jour des que le lecteur change de fuseau ; a midi,
 * elle resiste a onze heures d'ecart dans les deux sens.
 *
 * LE PROJET CLOS A DEBORDE, ET C'EST VOULU : son echeance est passee depuis
 * deux semaines et demie alors que ses dernieres heures datent de la semaine
 * derniere. Un jeu ou tout rentre dans les clous ne montre jamais a quoi
 * ressemble un projet qui a glisse.
 */
interface DemoProjet {
  demoKey: string;
  /** Nom du projet, champ SUR-MESURE : c'est lui qui titre la fiche. */
  libelle: string;
  /** La `demoKey` de son client. */
  client: string;
  /** Etat, en valeur STOCKEE du catalogue (`cadrage`, `enCours`...). */
  etat: string;
  /** Debut, en jours depuis le lundi de la semaine courante (negatif = passe). */
  debutEnJours: number;
  /** Echeance, meme repere. */
  echeanceEnJours: number;
  /** Budget en heures, champ NATIF de la famille (`budgetHours`, entier). */
  budgetHeures: number;
  description: string;
}

export const DEMO_PROJETS: ReadonlyArray<DemoProjet> = [
  /* EN COURS (1/2) : le gros du jeu, 14 entrees et 34 h 45 de temps saisi. */
  {
    demoKey: 'ts-pr-1',
    libelle: 'Terres Rouges, épisode 3',
    client: 'ts-cl-1',
    etat: 'enCours',
    debutEnJours: -42,
    echeanceEnJours: 28,
    budgetHeures: 120,
    description:
      'Troisième épisode de la série documentaire sur le bassin minier : montage, étalonnage et conformation avant diffusion.',
  },
  /* EN COURS (2/2) : 10 entrees et 17 h 30, l'autre moitié du volume. */
  {
    demoKey: 'ts-pr-2',
    libelle: 'Film institutionnel Belval',
    client: 'ts-cl-2',
    etat: 'enCours',
    debutEnJours: -28,
    echeanceEnJours: 14,
    budgetHeures: 60,
    description:
      'Film de six minutes pour la fondation : tournage dans les halls, interviews et habillage graphique.',
  },
  /* LIVRE : 3 entrees, 4 h. Le master est parti vendredi dernier. */
  {
    demoKey: 'ts-pr-3',
    libelle: 'Spot 30 secondes Clausener',
    client: 'ts-cl-3',
    etat: 'livre',
    debutEnJours: -70,
    echeanceEnJours: -3,
    budgetHeures: 24,
    description:
      'Spot de 30 secondes pour la gamme de saison, décliné en 16:9 et en format carré pour les réseaux.',
  },
  /* CLOS : 4 entrees, toutes dans la semaine PRECEDENTE, aucune cette semaine.
     C'est l'asymétrie que le fondateur a demandée : un projet dont plus rien
     ne bouge, mais dont les heures restent lisibles. */
  {
    demoKey: 'ts-pr-4',
    libelle: 'Captation Nuit des Musées',
    client: 'ts-cl-2',
    etat: 'clos',
    debutEnJours: -98,
    echeanceEnJours: -17,
    budgetHeures: 16,
    description:
      'Captation multicaméra de la soirée, montage court et sous-titrage. Clos : les masters ont été remis.',
  },
  /* CADRAGE : AUCUNE entrée. Sa carte « Heures » est vide, avec son libellé,
     et son total « Temps saisi » vaut zéro. */
  {
    demoKey: 'ts-pr-5',
    libelle: "Sentiers de l'Our",
    client: 'ts-cl-1',
    etat: 'cadrage',
    debutEnJours: 14,
    echeanceEnJours: 112,
    budgetHeures: 200,
    description:
      "Long documentaire sur la vallée de l'Our. Dossier d'aide déposé, aucune heure engagée tant que le financement n'est pas bouclé.",
  },
];

/* ------------------------------------------------------------------ */
/* Le journal : 33 entrees                                             */
/* ------------------------------------------------------------------ */

/**
 * TRENTE-TROIS CRENEAUX, sur DEUX semaines et DEUX personnes, du lundi au
 * vendredi entre 8 h et 18 h, plus UN samedi.
 *
 * AUCUN CHEVAUCHEMENT POUR UNE MEME PERSONNE : la grille de Ma semaine empile
 * les creneaux d'une journee, et deux creneaux qui se recouvrent y deviennent
 * illisibles sans rien signaler. Les journees sont donc ecrites en clair
 * ci-dessous, dans l'ordre, avec leur trou de midi.
 *
 * LES DUREES VONT DE 30 MIN A 3 H, PAR PAS DE 5 MIN : c'est ce que la saisie
 * accepte, et un jeu qui ne poserait que des heures rondes ne montrerait jamais
 * ce que l'habillage `duree` fait d'un 165.
 *
 * `projet` EST LA `demoKey` D'UN PROJET, jamais son nom ni un id : c'est la
 * table `demoKey -> id`, construite apres ecriture, qui pose le vrai
 * `projectId`, celui que le lien « heures-du-projet » suit. Chaque entree en
 * a un : une saisie de temps vise UNE tache, et une tache sert un projet
 * (decision du fondateur du 14/10/2026).
 */
interface DemoEntree {
  demoKey: string;
  /**
   * L'intervenant, par son ADRESSE. `workerId` (champ NATIF et REQUIS de la
   * famille) recoit le `personId` que l'annuaire lui associe, a l'import.
   */
  intervenant: string;
  /** `courante` ou `precedente` : la semaine ou tombe le creneau. */
  semaine: 'courante' | 'precedente';
  /** Jour de la semaine, 0 = lundi ... 5 = samedi. */
  jour: number;
  /** Heure locale de debut, entre 8 et 18. */
  heure: number;
  /** Minute de debut. */
  minute: number;
  /** Duree en minutes, entier (`durationMinutes`, champ NATIF). */
  duree: number;
  /** La `demoKey` de son projet. */
  projet: string;
  /**
   * CE SUR QUOI L'ON A TRAVAILLE : le TITRE D'UNE TACHE du projet. L'import
   * cree la tache (une par couple projet + titre) et l'entree la vise
   * (`taskId`), qui la titre. Le Service a quitte la saisie (14/10/2026).
   */
  tache: string;
  /** Facturable, champ NATIF. Vrai sur la production, surtout. */
  billable: boolean;
  notes?: string;
}

export const DEMO_ENTREES: ReadonlyArray<DemoEntree> = [
  /* ================= SEMAINE PRECEDENTE ================= */
  /* ---- Celia, 10 creneaux ---- */
  {
    demoKey: 'ts-en-01',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 0,
    heure: 9,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
    notes: "Séquence d'ouverture calée. Le reste attend la prise de son.",
  },
  {
    demoKey: 'ts-en-02',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 0,
    heure: 13,
    minute: 30,
    duree: 120,
    projet: 'ts-pr-4',
    tache: 'Derushage de la captation',
    billable: true,
  },
  {
    demoKey: 'ts-en-03',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 1,
    heure: 8,
    minute: 30,
    duree: 90,
    projet: 'ts-pr-4',
    tache: 'Montage de la captation',
    billable: true,
  },
  {
    demoKey: 'ts-en-04',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 1,
    heure: 10,
    minute: 15,
    duree: 150,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-05',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 2,
    heure: 9,
    minute: 0,
    duree: 60,
    projet: 'ts-pr-2',
    tache: 'Brief client',
    billable: true,
    notes: 'Message et minutage arrêtés avec la fondation.',
  },
  {
    demoKey: 'ts-en-06',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 2,
    heure: 10,
    minute: 30,
    duree: 180,
    projet: 'ts-pr-2',
    tache: 'Tournage Belval, hall 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-07',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 3,
    heure: 8,
    minute: 45,
    duree: 135,
    projet: 'ts-pr-1',
    tache: 'Etalonnage',
    billable: true,
  },
  /* SANS PROJET (1/2) : l'administratif de la boîte ne se refacture pas. */
  {
    demoKey: 'ts-en-08',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 3,
    heure: 14,
    minute: 0,
    duree: 45,
    projet: 'ts-pr-2',
    tache: 'Notes de frais et plannings',
    billable: false,
  },
  {
    demoKey: 'ts-en-09',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 4,
    heure: 9,
    minute: 0,
    duree: 120,
    projet: 'ts-pr-3',
    tache: 'Livraison',
    billable: true,
    notes: 'Master 16:9 et version carrée déposés sur le serveur du client.',
  },
  {
    demoKey: 'ts-en-10',
    intervenant: CELIA,
    semaine: 'precedente',
    jour: 4,
    heure: 13,
    minute: 0,
    duree: 90,
    projet: 'ts-pr-4',
    tache: 'Livraison des masters',
    billable: false,
    notes: 'Dernier geste du projet : il est clos depuis.',
  },
  /* ---- Marc, 6 creneaux ---- */
  {
    demoKey: 'ts-en-11',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 0,
    heure: 8,
    minute: 30,
    duree: 120,
    projet: 'ts-pr-2',
    tache: 'Reperages',
    billable: true,
    notes: 'Hall 3 et passerelle, lumière du matin.',
  },
  {
    demoKey: 'ts-en-12',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 0,
    heure: 11,
    minute: 0,
    duree: 150,
    projet: 'ts-pr-1',
    tache: 'Prise de son plateau',
    billable: true,
  },
  {
    demoKey: 'ts-en-13',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 1,
    heure: 9,
    minute: 0,
    duree: 90,
    projet: 'ts-pr-4',
    tache: 'Sous-titrage de la captation',
    billable: true,
  },
  {
    demoKey: 'ts-en-14',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 2,
    heure: 8,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-15',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 3,
    heure: 10,
    minute: 0,
    duree: 60,
    projet: 'ts-pr-3',
    tache: 'Retours client sur le spot',
    billable: false,
  },
  {
    demoKey: 'ts-en-16',
    intervenant: MARC,
    semaine: 'precedente',
    jour: 4,
    heure: 8,
    minute: 30,
    duree: 75,
    projet: 'ts-pr-2',
    tache: 'Trajet Luxembourg-Belval',
    billable: false,
  },

  /* ================= SEMAINE COURANTE ================= */
  /* ---- Celia, 9 creneaux ---- */
  {
    demoKey: 'ts-en-17',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 0,
    heure: 9,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-18',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 0,
    heure: 13,
    minute: 0,
    duree: 90,
    projet: 'ts-pr-2',
    tache: 'Etalonnage',
    billable: true,
  },
  {
    demoKey: 'ts-en-19',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 1,
    heure: 8,
    minute: 30,
    duree: 150,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-20',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 1,
    heure: 11,
    minute: 30,
    duree: 45,
    projet: 'ts-pr-1',
    tache: 'Trajet studio Belval',
    billable: false,
  },
  {
    demoKey: 'ts-en-21',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 1,
    heure: 14,
    minute: 0,
    duree: 60,
    projet: 'ts-pr-2',
    tache: 'Brief client',
    billable: true,
  },
  {
    demoKey: 'ts-en-22',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 2,
    heure: 9,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-1',
    tache: 'Etalonnage',
    billable: true,
    notes: 'Passe complète sur les extérieurs, à revoir avec le chef opérateur.',
  },
  {
    demoKey: 'ts-en-23',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 3,
    heure: 8,
    minute: 45,
    duree: 120,
    projet: 'ts-pr-2',
    tache: 'Montage du film institutionnel',
    billable: true,
  },
  /* SANS PROJET (2/2), ET LE PLUS COURT DU JEU : 30 min, le plancher. */
  {
    demoKey: 'ts-en-24',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 3,
    heure: 13,
    minute: 30,
    duree: 30,
    projet: 'ts-pr-1',
    tache: "Point d'equipe",
    billable: false,
  },
  {
    demoKey: 'ts-en-25',
    intervenant: CELIA,
    semaine: 'courante',
    jour: 4,
    heure: 9,
    minute: 15,
    duree: 165,
    projet: 'ts-pr-1',
    tache: 'Conformation episode 3',
    billable: true,
  },
  /* ---- Marc, 8 creneaux, dont LE SAMEDI ---- */
  {
    demoKey: 'ts-en-26',
    intervenant: MARC,
    semaine: 'courante',
    jour: 0,
    heure: 8,
    minute: 0,
    duree: 90,
    projet: 'ts-pr-2',
    tache: 'Reperages',
    billable: true,
  },
  {
    demoKey: 'ts-en-27',
    intervenant: MARC,
    semaine: 'courante',
    jour: 0,
    heure: 10,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-1',
    tache: 'Prise de son plateau',
    billable: true,
  },
  {
    demoKey: 'ts-en-28',
    intervenant: MARC,
    semaine: 'courante',
    jour: 1,
    heure: 9,
    minute: 0,
    duree: 120,
    projet: 'ts-pr-1',
    tache: 'Montage episode 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-29',
    intervenant: MARC,
    semaine: 'courante',
    jour: 2,
    heure: 8,
    minute: 30,
    duree: 75,
    projet: 'ts-pr-2',
    tache: 'Trajet Luxembourg-Belval',
    billable: false,
  },
  {
    demoKey: 'ts-en-30',
    intervenant: MARC,
    semaine: 'courante',
    jour: 2,
    heure: 10,
    minute: 0,
    duree: 180,
    projet: 'ts-pr-2',
    tache: 'Tournage Belval, hall 3',
    billable: true,
  },
  {
    demoKey: 'ts-en-31',
    intervenant: MARC,
    semaine: 'courante',
    jour: 3,
    heure: 9,
    minute: 0,
    duree: 150,
    projet: 'ts-pr-1',
    tache: 'Habillage graphique',
    billable: true,
  },
  {
    demoKey: 'ts-en-32',
    intervenant: MARC,
    semaine: 'courante',
    jour: 4,
    heure: 10,
    minute: 0,
    duree: 60,
    projet: 'ts-pr-3',
    tache: 'Relecture du spot avec le client',
    billable: false,
  },
  /* LE SEUL CRENEAU DE WEEK-END DU JEU, et il n'est pas decoratif : la grille
     de Ma semaine replie le samedi et le dimanche, et un week-end vide ne
     montrerait jamais ce que le depliage sert a voir. Une seule personne l'a
     travaille : le dimanche, lui, reste vide. */
  {
    demoKey: 'ts-en-33',
    intervenant: MARC,
    semaine: 'courante',
    jour: 5,
    heure: 10,
    minute: 0,
    duree: 120,
    projet: 'ts-pr-1',
    tache: 'Rattrapage montage episode 3',
    billable: true,
    notes: 'Rattrapage du retard pris sur la conformation.',
  },
];

/* ------------------------------------------------------------------ */
/* Les dates, calculees a l'import                                     */
/* ------------------------------------------------------------------ */

/**
 * LE LUNDI DE LA SEMAINE DE `reference`, A MINUIT, EN HEURE LOCALE.
 *
 * Tout le jeu s'accroche a ce point : le journal tombe sur la semaine courante
 * et la precedente, les projets sur des multiples de sept jours autour.
 *
 * PAS DE `toISOString().slice(...)` : `toISOString` rend de l'UTC, et pour qui
 * vit a l'est de Greenwich le lundi 00:00 local y est encore dimanche. La
 * semaine entiere glisserait d'un cran, sans la moindre erreur. On ne lit donc
 * que des accesseurs LOCAUX (`getFullYear`, `getMonth`, `getDate`, `getDay`).
 */
function lundiDeLaSemaine(reference: Date): Date {
  const lundi = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  /* getDay() : 0 = dimanche, 1 = lundi ... 6 = samedi. Le decalage jusqu'au
     lundi precedent vaut donc (jour + 6) % 7, dimanche compris (6 jours). */
  lundi.setDate(lundi.getDate() - ((lundi.getDay() + 6) % 7));
  return lundi;
}

/**
 * L'INSTANT d'un creneau : le lundi de reference, plus des jours, a une heure
 * LOCALE donnee.
 *
 * Le constructeur `Date(annee, mois, jour, ...)` accepte un numero de jour qui
 * deborde du mois (le 35 fevrier est le 7 mars) et pose une heure de MUR : le
 * changement d'heure d'automne ou de printemps ne decale donc pas les creneaux
 * d'une heure, contrairement a une addition de millisecondes.
 */
function instantLocal(lundi: Date, jours: number, heure: number, minute: number): Date {
  return new Date(lundi.getFullYear(), lundi.getMonth(), lundi.getDate() + jours, heure, minute, 0, 0);
}

/**
 * MIDI LOCAL, le jour dit. Pour les deux dates d'un PROJET, qui sont des jours
 * et pas des rendez-vous : minuit glisse d'un jour des qu'on change de fuseau,
 * midi tient jusqu'a onze heures d'ecart dans les deux sens.
 */
function midiLocal(lundi: Date, jours: number): Date {
  return instantLocal(lundi, jours, 12, 0);
}

/* ------------------------------------------------------------------ */
/* L'import                                                            */
/* ------------------------------------------------------------------ */

/** Le nombre total de lignes que ce jeu pose, cite par le hub Parametres. */
export const DEMO_TOTAL =
  DEMO_CLIENTS.length + DEMO_PROJETS.length + tachesDeDemo().length + DEMO_ENTREES.length;

/** Les `demoKey` deja en base pour une liste de fiches, quel que soit le jeu. */
function clefsConnues(fiches: ReadonlyArray<{ customFields?: Record<string, unknown> }>): Set<string> {
  const connues = new Set<string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') connues.add(clef);
  }
  return connues;
}

/** La table `demoKey -> id` d'une liste deja en base : de quoi resoudre un lien. */
function idsParClef(
  fiches: ReadonlyArray<{ id: string; customFields?: Record<string, unknown> }>,
): Map<string, string> {
  const table = new Map<string, string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') table.set(clef, fiche.id);
  }
  return table;
}

/** Un compte rendu qui se lit : « 41 lignes créées » plutôt qu'un nombre nu. */
const lignes = (n: number): string => `${n} ${n > 1 ? 'lignes' : 'ligne'}`;

/**
 * LE SAC DE CHAMPS D'UN PROJET, sans les clefs vides.
 *
 * UN CHAMP METIER SANS VALEUR S'OMET, il ne se met pas a `null` : le socle le
 * declare facultatif, et un `null` en base ferait echouer la lecture de toute
 * la famille pour les autres apps de l'organisation. Ici les trois champs sont
 * toujours poses, la regle vaut pour la suite.
 */
function sacDeProjet(item: DemoProjet): CustomFieldValues {
  return {
    libelle: item.libelle,
    etat: item.etat,
    description: item.description,
    demoKey: item.demoKey,
  };
}

/** Le sac d'une entree de journal, meme regle : `notes` s'omet quand il manque. */
function sacDEntree(item: DemoEntree): CustomFieldValues {
  return {
    ...(item.notes === undefined ? {} : { notes: item.notes }),
    demoKey: item.demoKey,
  };
}

/** La `demoKey` de la tache d'un couple projet + titre : stable d'un import a l'autre. */
export function clefDeTache(projet: string, titre: string): string {
  const mot = titre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${projet.replace(/^ts-pr-/, 'ts-ta-')}-${mot}`;
}

/** Les taches du jeu : une par couple projet + titre, dans l'ordre des entrees. */
export function tachesDeDemo(): { demoKey: string; projet: string; libelle: string }[] {
  const taches = new Map<string, { demoKey: string; projet: string; libelle: string }>();
  for (const entree of DEMO_ENTREES) {
    const demoKey = clefDeTache(entree.projet, entree.tache);
    if (!taches.has(demoKey)) taches.set(demoKey, { demoKey, projet: entree.projet, libelle: entree.tache });
  }
  return [...taches.values()];
}

/**
 * ECRIRE LE JEU DANS L'ORGANISATION COURANTE, et ne creer que ce qui manque.
 *
 * TROIS JEUX, DANS CET ORDRE, ET IL N'EST PAS LIBRE : un projet pointe son
 * client par `contactId`, une entree pointe son projet par `projectId`. On cree
 * donc les clients d'abord, on relit leur id REEL, puis les projets, on relit
 * les leurs, puis le journal. Un import RELANCE retrouve les memes documents
 * par leur `demoKey`, donc les liens restent justes meme quand rien n'est
 * recree.
 *
 * UNE REFERENCE INTROUVABLE N'EST PAS INVENTEE : le projet se cree SANS son
 * `contactId`, l'entree SANS son `projectId`, plutot qu'avec un id fantome. La
 * ligne apparait alors avec une case vide, ce qui est visible, plutot que de
 * pointer un document qui n'existe pas, ce qui ne l'est pas.
 *
 * UN SEUL POINT DE DEPART POUR TOUTES LES DATES : le lundi de la semaine
 * courante, calcule UNE fois ici. Un import qui prendrait `new Date()` a chaque
 * ligne pourrait franchir minuit en cours de route et poser deux semaines
 * differentes dans le meme jeu.
 */
export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<DemoImportResult> {
  let created = 0;
  let skipped = 0;
  const lundi = lundiDeLaSemaine(new Date());

  /* ---- 1. Les clients (contacts::organization) ---- */
  const clientsExistants = await sdk.contacts.list({ type: TYPE_SOCIETE });
  const clientsConnus = clefsConnues(clientsExistants);
  for (const item of DEMO_CLIENTS) {
    if (clientsConnus.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    await sdk.contacts.create({
      orgId,
      type: TYPE_SOCIETE,
      name: item.name,
      email: item.email,
      phone: item.phone,
      customFields: { relation: item.relation, demoKey: item.demoKey },
    });
    created += 1;
  }
  /* On relit APRES ecriture : c'est cette table qui donne les id reels aux
     projets, y compris ceux qu'on vient de creer. */
  const clients = idsParClef(await sdk.contacts.list({ type: TYPE_SOCIETE }));

  /* ---- 2. Les projets (projects::projet) ---- */
  const projetsExistants = await sdk.projects.list({ type: TYPE_PROJET });
  const projetsConnus = clefsConnues(projetsExistants);
  for (const item of DEMO_PROJETS) {
    if (projetsConnus.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const contactId = clients.get(item.client);
    await sdk.projects.create({
      orgId,
      type: TYPE_PROJET,
      ...(contactId === undefined ? {} : { contactId }),
      /* PAS DE `status`, ET C'EST UN CHOIX : la famille porte bien un champ
         natif d'avancement, mais la declaration ne cite QUE le champ sur-mesure
         `etat` (c'est lui que le kanban ecrit quand on depose une carte).
         Poser les deux ecrirait le meme fait a deux endroits, et le jour ou
         quelqu'un deplace une carte, seul `etat` bougerait : le jeu de
         demonstration naitrait avec une divergence deja en place. */
      startAt: midiLocal(lundi, item.debutEnJours),
      dueAt: midiLocal(lundi, item.echeanceEnJours),
      budgetHours: item.budgetHeures,
      customFields: sacDeProjet(item),
    });
    created += 1;
  }
  const projets = idsParClef(await sdk.projects.list({ type: TYPE_PROJET }));

  /* ---- 3. Les taches des projets (tasks::tache, celles de Projets) ---- */
  /* UNE TACHE PAR COUPLE PROJET + TITRE des entrees : le clic sur un projet
     les montre, et le choix « Projet / Tâche » de la saisie les propose. Une
     tache dont le projet manque ne se cree pas. */
  const tachesExistantes = await sdk.tasks.list({ type: TYPE_TACHE });
  const tachesConnues = clefsConnues(tachesExistantes);
  for (const item of tachesDeDemo()) {
    if (tachesConnues.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const projectId = projets.get(item.projet);
    if (projectId === undefined) continue;
    await sdk.tasks.create({
      orgId,
      type: TYPE_TACHE,
      projectId,
      status: 'enCours',
      customFields: { libelle: item.libelle, demoKey: item.demoKey },
    });
    created += 1;
  }
  const taches = idsParClef(await sdk.tasks.list({ type: TYPE_TACHE }));

  /* ---- 4. Le journal (timesheets::journal) ---- */
  /* L'INTERVENANT EST UNE PERSONNE DE L'ANNUAIRE, retrouvee par son adresse.
     Une adresse que l'annuaire ne connait pas (une org sans ces comptes de
     demonstration) ne s'invente pas : l'entree est SAUTEE, et le compte rendu
     le dit, plutot que d'ecrire une adresse la ou le socle attend une personne. */
  const parAdresse = new Map(
    (await sdk.personnes.list({ archived: 'all' })).map((personne) => [
      personne.email.toLowerCase(),
      personne.personId,
    ]),
  );
  const inconnus = new Set<string>();
  let sansIntervenant = 0;
  const entreesExistantes = await sdk.timesheets.list({ type: TYPE_ENTREE });
  const entreesConnues = clefsConnues(entreesExistantes);
  for (const item of DEMO_ENTREES) {
    if (entreesConnues.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const workerId = parAdresse.get(item.intervenant);
    if (workerId === undefined) {
      sansIntervenant += 1;
      inconnus.add(item.intervenant);
      continue;
    }
    const projectId = projets.get(item.projet);
    const taskId = projectId === undefined ? undefined : taches.get(clefDeTache(item.projet, item.tache));
    const jours = item.jour + (item.semaine === 'precedente' ? -7 : 0);
    await sdk.timesheets.create({
      orgId,
      type: TYPE_ENTREE,
      workerId,
      startAt: instantLocal(lundi, jours, item.heure, item.minute),
      durationMinutes: item.duree,
      billable: item.billable,
      ...(projectId === undefined ? {} : { projectId }),
      ...(taskId === undefined ? {} : { taskId }),
      customFields: sacDEntree(item),
    });
    created += 1;
  }

  /* LE COMPTE RENDU DIT LES DEUX NOMBRES, et il se lit a voix haute : ce qui
     vient d'etre ecrit, et ce qui etait deja la. Le second import ne dit donc
     pas « 0 ligne creee », il dit que le jeu est en place. */
  const dejaLa =
    skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const bilan =
    created === 0 && sansIntervenant === 0
      ? `Le jeu de démonstration est déjà en place (${lignes(skipped)}).`
      : `${lignes(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`;
  const message =
    sansIntervenant === 0
      ? bilan
      : `${bilan} ${sansIntervenant} ${sansIntervenant > 1 ? 'entrées sautées' : 'entrée sautée'} : intervenant absent de l'annuaire (${[...inconnus].join(', ')}).`;

  return { created, skipped, message };
}
